import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Collapse,
  Checkbox,
  Row,
  Col,
  Input,
  Button,
  Progress,
  Space
} from '@lockerpm/design';

import {
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";

import {
  green
} from '@ant-design/colors';

import CollapseHeader from "./CollapseHeader";

import commonComponents from "../../../../components/common";

import rewardServices from "../../../../services/reward";
import global from "../../../../config/global";

const ExtInstallation = (props) => {
  const { AllBrowsers } = commonComponents;
  const allBrowsers = global.constants.BROWSERS
  const { t } = useTranslation();
  const stepNumber = 3;

  const {
    className,
    missionSteps = [],
    mission = {},
    verify = () => {},
    resubmit = () => {}
  } = props;

  const [collapseKeys, setCollapseKeys] = useState([])
  const [callingAPI, setCallingAPI] = useState(false)
  const [selectedBrowsers, setSelectedBrowsers] = useState([])
  const [allBrowsersData, setAllBrowsersData] = useState([])
  const [stepIndex, setStepIndex] = useState(0)
  const [percent, setPercent] = useState(0)

  const missionStep = useMemo(() => {
    const step = missionSteps.find(
      s => s.value === mission?.status
    )
    return step || missionSteps[0]
  }, [mission])

  useEffect(() => {
    fetchData();
  }, [mission]);

  useEffect(() => {
    if (missionStep.key === 1) {
      setStepIndex(0)
    } else {
      setStepIndex(stepNumber - 1)
    }
  }, [missionStep])

  useEffect(() => {
    if (missionStep.key === 3) {
      setPercent(100)
    } else {
      setPercent(stepIndex / stepNumber * 100)
    }
  }, [stepIndex, missionStep])

  const fetchData = () => {
    const sBrowsers = (mission?.answer || []).map(a => a.browser);
    setSelectedBrowsers(sBrowsers);
    setAllBrowsersData(allBrowsers.map(b => ({
      value: b.value,
      displayName: (mission?.answer || []).find(a => a.browser === b.value)?.user_identifier || ''
    })))
  }

  const selectedBrowsersInfo = useMemo(() => {
    return selectedBrowsers.map(b => {
      return allBrowsersData.find(browser => browser.value === b)
    }).filter(b => b.displayName)
  }, [selectedBrowsers, allBrowsersData])

  const handleSend = async () => {
    const action = t('rewards.ext_installation.title');
    const store = 'extension';
    setCallingAPI(true);
    await rewardServices.mission_completed(
      mission.mission.id,
      selectedBrowsersInfo.map((d) => ({
        user_identifier: d.displayName,
        browser: d.value
      }))
    ).then((res) => {
      if (res.claim) {
        verify({
          action,
          percent: 5,
        })
      } else {
        resubmit({
          action,
          store,
        })
      }
    }).catch(() => {
      resubmit({
        action,
        store,
      })
    })
    setCallingAPI(false);
  }

  return (
    <div className={`ext-installation ${className}`}>
      <Collapse
        activeKey={collapseKeys}
        expandIconPosition={'end'}
        onChange={setCollapseKeys}
      >
        <Collapse.Panel
          key="1"
          header={<CollapseHeader
            title={t('rewards.ext_installation.title')}
            percent={5}
            missionStep={missionStep}
            collapseKeys={collapseKeys}
          />}
        >
          <div>
            <div className="lg:w-full md:w-full mb-4">
              {t('rewards.ext_installation.subtitle')}
            </div>
            <AllBrowsers />
            <div className={`mission-steps ${className}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <p className="text-l font-semibold mr-4">{t('common.steps')}:</p>
                  <Progress
                    steps={stepNumber}
                    percent={percent}
                    showInfo={false}
                    strokeColor={green[6]}
                  />
                </div>
                {
                  missionStep.key !== 3 && <Space size={[12, 12]}>
                    <Button
                      type="text"
                      disabled={stepIndex === 0}
                      icon={<LeftOutlined />}
                      onClick={() => setStepIndex(stepIndex - 1)}
                    />
                    <Button
                      type="text"
                      disabled={stepIndex === stepNumber - 1}
                      icon={<RightOutlined />}
                      onClick={() => setStepIndex(stepIndex + 1)}
                    />
                  </Space>
                }
              </div>
              {
                stepIndex === 0 && <p>{t('rewards.ext_installation.step1')}</p>
              }
              {
                stepIndex === 1 && <p>{t('rewards.ext_installation.step2')}</p>
              }
              {
                stepIndex === 2 &&  <div>
                  <p>{t('rewards.ext_installation.step3')}</p>
                  <div className="mt-4">
                    <Checkbox.Group
                      className="w-full"
                      value={selectedBrowsers}
                      disabled={missionStep.key === 3}
                      onChange={(v) => setSelectedBrowsers(v)}
                    >
                      <Row gutter={[12, 12]}>
                        {
                          allBrowsers.map((b, index) => <Col lg={12} sm={24} xs={24} key={index}>
                            <Row gutter={[12, 12]} className="flex items-center">
                              <Col span={6}>
                                <Checkbox
                                  value={b.value}
                                  disabled={!b.active}
                                >
                                  {b.name}
                                </Checkbox>
                              </Col>
                              <Col span={18}>
                                {
                                  selectedBrowsers.includes(b.value) && <Input
                                    style={{ width: '50%' }}
                                    value={allBrowsersData.find((d) => d.value === b.value)?.displayName}
                                    disabled={callingAPI || missionStep.key === 3}
                                    placeholder={t('rewards.ext_installation.input_placeholder')}
                                    onChange={(e) => setAllBrowsersData(allBrowsersData.map((data) => ({
                                      ...data,
                                      displayName: data.value === b.value ? e.target.value : data.displayName
                                    })))}
                                  />
                                }
                              </Col>
                            </Row>
                          </Col>)
                        }
                      </Row>
                    </Checkbox.Group>
                    {
                      missionStep.key < 3 && <Button
                        className="mt-4"
                        disabled={selectedBrowsersInfo.length < 2}
                        type={selectedBrowsersInfo.length < 2 ? undefined : 'primary'}
                        onClick={() => handleSend()}
                      >
                        {t('rewards.ext_installation.btn')}
                      </Button>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default ExtInstallation;